import React, { useState, useMemo } from 'react';
import SCGRuleLink from './SCGRuleLink';
import ViewInPdfButton from './ViewInPdfButton';
import { submitFeedback } from '../services/api';
import {
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon,
  CheckIcon,
  XMarkIcon,
  DocumentTextIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

/**
 * TrackChangesViewer - Word-style track changes display for tearline sanitization.
 *
 * Dark-themed for The Vault UI. Renders inline diffs with red strikethrough
 * (removals) and green highlights (insertions), margin annotations with SCG
 * rule citations, and accept/reject per change.
 */
export default function TrackChangesViewer({ originalText, sanitizedText, changes, statistics, onApplyChanges, provenanceDecisionId }) {
  const [acceptedChanges, setAcceptedChanges] = useState(new Set());
  const [rejectedChanges, setRejectedChanges] = useState(new Set());
  const [filterMethod, setFilterMethod] = useState('all');
  const [showStats, setShowStats] = useState(false);
  const [selectedChange, setSelectedChange] = useState(null);

  // Filter changes by sanitization method
  const filteredChanges = useMemo(() => {
    if (!changes) return [];
    if (filterMethod === 'all') return changes;
    return changes.filter(c => c.sanitization_method === filterMethod);
  }, [changes, filterMethod]);

  // Get unique sanitization methods for filter
  const methods = useMemo(() => {
    if (!changes) return [];
    const methodSet = new Set(changes.map(c => c.sanitization_method));
    return Array.from(methodSet);
  }, [changes]);

  // Build the inline document view with track changes
  const renderInlineDocument = () => {
    if (!originalText || !changes || changes.length === 0) {
      return <p className="text-sm text-gray-300 whitespace-pre-wrap">{sanitizedText || originalText}</p>;
    }

    // Sort changes by original_start position
    const sortedChanges = [...changes].sort((a, b) => a.original_start - b.original_start);

    const elements = [];
    let lastEnd = 0;

    sortedChanges.forEach((change, idx) => {
      const isAccepted = acceptedChanges.has(change.id);
      const isRejected = rejectedChanges.has(change.id);
      const isFiltered = filterMethod !== 'all' && change.sanitization_method !== filterMethod;
      const isSelected = selectedChange === change.id;

      // Add unchanged text before this change
      if (change.original_start > lastEnd) {
        elements.push(
          <span key={`text-${idx}`} className="text-gray-200">
            {originalText.slice(lastEnd, change.original_start)}
          </span>
        );
      }

      if (isFiltered) {
        // Show original text for filtered-out changes
        elements.push(
          <span key={`filtered-${idx}`} className="text-gray-200">
            {change.original_text}
          </span>
        );
      } else if (isRejected) {
        // Rejected: show original text only
        elements.push(
          <span key={`rejected-${idx}`} className="text-gray-300 bg-gray-700/50 rounded px-0.5">
            {change.original_text}
          </span>
        );
      } else if (isAccepted) {
        // Accepted: show replacement text only
        elements.push(
          <span key={`accepted-${idx}`} className="text-emerald-300 bg-emerald-900/40 rounded px-0.5">
            {change.replacement_text}
          </span>
        );
      } else {
        // Pending: show both with track changes styling
        elements.push(
          <span
            key={`change-${idx}`}
            className={`relative inline cursor-pointer rounded ${isSelected ? 'ring-2 ring-blue-400' : ''}`}
            onClick={() => setSelectedChange(isSelected ? null : change.id)}
          >
            {/* Original text with strikethrough */}
            {change.original_text && (
              <span className="line-through text-red-400 bg-red-900/30 decoration-red-500 decoration-2 rounded px-0.5">
                {change.original_text}
              </span>
            )}
            {/* Replacement text with highlight */}
            {change.replacement_text && (
              <span className="text-emerald-300 bg-emerald-900/40 font-medium rounded px-0.5">
                {change.replacement_text}
              </span>
            )}
            {/* Change number badge */}
            <sup className="text-[10px] text-blue-400 font-bold ml-0.5">{change.id}</sup>
          </span>
        );
      }

      lastEnd = change.original_end;
    });

    // Add remaining text after last change
    if (lastEnd < originalText.length) {
      elements.push(
        <span key="text-end" className="text-gray-200">
          {originalText.slice(lastEnd)}
        </span>
      );
    }

    return <div className="text-sm leading-relaxed whitespace-pre-wrap">{elements}</div>;
  };

  const handleAccept = (changeId) => {
    setAcceptedChanges(prev => {
      const next = new Set(prev);
      next.add(changeId);
      return next;
    });
    setRejectedChanges(prev => {
      const next = new Set(prev);
      next.delete(changeId);
      return next;
    });
  };

  const handleReject = (changeId) => {
    setRejectedChanges(prev => {
      const next = new Set(prev);
      next.add(changeId);
      return next;
    });
    setAcceptedChanges(prev => {
      const next = new Set(prev);
      next.delete(changeId);
      return next;
    });
  };

  const handleAcceptAll = () => {
    const allIds = new Set(filteredChanges.map(c => c.id));
    setAcceptedChanges(allIds);
    setRejectedChanges(new Set());
  };

  const handleRejectAll = () => {
    const allIds = new Set(filteredChanges.map(c => c.id));
    setRejectedChanges(allIds);
    setAcceptedChanges(new Set());
  };

  const handleReset = () => {
    setAcceptedChanges(new Set());
    setRejectedChanges(new Set());
  };

  const pendingCount = filteredChanges.filter(c => !acceptedChanges.has(c.id) && !rejectedChanges.has(c.id)).length;

  const handleApplyChanges = () => {
    if (!originalText || !changes || changes.length === 0) return;

    // Sort changes by original_start descending so we can splice from end to start
    const sortedChanges = [...changes].sort((a, b) => b.original_start - a.original_start);
    let resultText = originalText;

    for (const change of sortedChanges) {
      const isRejected = rejectedChanges.has(change.id);
      if (isRejected) {
        // Keep original text — no change
        continue;
      }
      // Accepted or pending: apply the replacement
      const before = resultText.slice(0, change.original_start);
      const after = resultText.slice(change.original_end);
      resultText = before + (change.replacement_text || '') + after;
    }

    // Persist per-change feedback to backend
    const feedbackEntries = changes
      .filter(c => acceptedChanges.has(c.id) || rejectedChanges.has(c.id))
      .map(c => ({
        pipeline_type: 'tearline',
        document_text: originalText || '',
        decision: acceptedChanges.has(c.id) ? 'accept' : 'reject',
        element_id: String(c.id),
        original_text: c.original_text || null,
        replacement_text: c.replacement_text || null,
        metadata: {
          sanitization_method: c.sanitization_method || null,
          scg_rule: c.scg_rule_id || null,
          ...(provenanceDecisionId ? { source_decision_id: provenanceDecisionId } : {}),
        },
      }));

    if (feedbackEntries.length > 0) {
      submitFeedback(feedbackEntries).catch(err =>
        console.error('Failed to persist tearline feedback:', err)
      );
    }

    if (onApplyChanges) {
      onApplyChanges({
        resultText,
        acceptedCount: acceptedChanges.size,
        rejectedCount: rejectedChanges.size,
        pendingCount: changes.length - acceptedChanges.size - rejectedChanges.size
      });
    }
  };

  if (!changes || changes.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-600 p-6 text-center">
        <DocumentTextIcon className="w-10 h-10 text-gray-500 mx-auto mb-2" />
        <p className="text-gray-400">No changes detected between original and sanitized text.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-gray-800 rounded-lg border border-gray-600 p-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-gray-200">
              {changes.length} change{changes.length !== 1 ? 's' : ''} detected
            </span>
            <span className="text-xs text-gray-400">
              ({pendingCount} pending, {acceptedChanges.size} accepted, {rejectedChanges.size} rejected)
            </span>
          </div>

          <div className="flex items-center space-x-2">
            {/* Filter dropdown */}
            <div className="flex items-center space-x-1">
              <FunnelIcon className="w-4 h-4 text-gray-400" />
              <select
                value={filterMethod}
                onChange={(e) => setFilterMethod(e.target.value)}
                className="text-xs bg-gray-700 border-gray-600 text-gray-200 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="all">All Methods</option>
                {methods.map(m => (
                  <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
                ))}
              </select>
            </div>

            {/* Stats toggle */}
            <button
              onClick={() => setShowStats(!showStats)}
              className="p-1.5 text-gray-400 hover:text-gray-200 rounded"
              title="Toggle statistics"
            >
              <ChartBarIcon className="w-4 h-4" />
            </button>

            {/* Bulk actions */}
            <button
              onClick={handleAcceptAll}
              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-emerald-900/60 text-emerald-300 hover:bg-emerald-800/60 border border-emerald-700/50"
            >
              <CheckIcon className="w-3 h-3 mr-1" />
              Accept All
            </button>
            <button
              onClick={handleRejectAll}
              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-red-900/60 text-red-300 hover:bg-red-800/60 border border-red-700/50"
            >
              <XMarkIcon className="w-3 h-3 mr-1" />
              Reject All
            </button>
            <button
              onClick={handleReset}
              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600"
            >
              Reset
            </button>
            {onApplyChanges && (
              <button
                onClick={handleApplyChanges}
                className="inline-flex items-center px-3 py-1 text-xs font-medium rounded bg-indigo-600 text-white hover:bg-indigo-700 ml-2"
              >
                <CheckIcon className="w-3 h-3 mr-1" />
                Apply Changes
              </button>
            )}
          </div>
        </div>

        {/* Statistics panel */}
        {showStats && statistics && (
          <div className="mt-3 pt-3 border-t border-gray-600 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="text-center">
              <div className="text-lg font-bold text-gray-100">{statistics.total_changes}</div>
              <div className="text-xs text-gray-400">Total Changes</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-blue-400">{statistics.replacements}</div>
              <div className="text-xs text-gray-400">Replacements</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-red-400">{statistics.removals}</div>
              <div className="text-xs text-gray-400">Removals</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-amber-400">{statistics.content_reduction_pct}%</div>
              <div className="text-xs text-gray-400">Content Reduction</div>
            </div>
          </div>
        )}
      </div>

      {/* Main content area: document + sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Document with inline changes */}
        <div className="lg:col-span-2 bg-gray-900 rounded-lg border border-gray-600 p-4 max-h-[600px] overflow-y-auto">
          {renderInlineDocument()}
        </div>

        {/* Changes sidebar */}
        <div className="bg-gray-800 rounded-lg border border-gray-600 p-3 max-h-[600px] overflow-y-auto">
          <h4 className="text-sm font-medium text-gray-200 mb-3">Change Details</h4>
          <div className="space-y-2">
            {filteredChanges.map((change) => {
              const isAccepted = acceptedChanges.has(change.id);
              const isRejected = rejectedChanges.has(change.id);
              const isSelected = selectedChange === change.id;

              return (
                <div
                  key={change.id}
                  className={`p-2 rounded-lg border text-xs cursor-pointer transition-colors ${
                    isSelected ? 'border-blue-500 bg-blue-900/30' :
                    isAccepted ? 'border-emerald-600 bg-emerald-900/20' :
                    isRejected ? 'border-red-700 bg-red-900/20 opacity-60' :
                    'border-gray-600 hover:border-gray-500 bg-gray-700/30'
                  }`}
                  onClick={() => setSelectedChange(isSelected ? null : change.id)}
                >
                  {/* Change header */}
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center space-x-1.5">
                      <span className="font-bold text-blue-400">#{change.id}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        change.type === 'replacement' ? 'bg-blue-900/50 text-blue-300' :
                        change.type === 'removal' ? 'bg-red-900/50 text-red-300' :
                        'bg-emerald-900/50 text-emerald-300'
                      }`}>
                        {change.type}
                      </span>
                      <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-600 text-gray-300">
                        {change.sanitization_method}
                      </span>
                    </div>

                    {/* Accept/Reject buttons */}
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleAccept(change.id); }}
                        className={`p-0.5 rounded ${isAccepted ? 'text-emerald-400 bg-emerald-900/40' : 'text-gray-500 hover:text-emerald-400'}`}
                        title="Accept change"
                      >
                        <CheckCircleIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleReject(change.id); }}
                        className={`p-0.5 rounded ${isRejected ? 'text-red-400 bg-red-900/40' : 'text-gray-500 hover:text-red-400'}`}
                        title="Reject change"
                      >
                        <XCircleIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Change content */}
                  {change.original_text && (
                    <div className="mb-1">
                      <span className="text-gray-400">Remove: </span>
                      <span className="line-through text-red-400">{change.original_text.slice(0, 80)}{change.original_text.length > 80 ? '...' : ''}</span>
                    </div>
                  )}
                  {change.replacement_text && (
                    <div className="mb-1">
                      <span className="text-gray-400">Add: </span>
                      <span className="text-emerald-300">{change.replacement_text.slice(0, 80)}{change.replacement_text.length > 80 ? '...' : ''}</span>
                    </div>
                  )}

                  {/* SCG rule citation */}
                  {change.scg_rule && (
                    <div className="mt-1 pt-1 border-t border-gray-600">
                      <span className="text-gray-400">SCG Rule: </span>
                      <span className="font-medium text-indigo-400 inline-flex items-center gap-1">
                        <SCGRuleLink ruleId={change.scg_rule} />
                        <ViewInPdfButton ruleId={change.scg_rule} variant="icon" size="sm" />
                      </span>
                      {change.scg_classification && (
                        <span className="ml-1 text-gray-500">({change.scg_classification})</span>
                      )}
                      {change.scg_description && (
                        <p className="text-gray-500 mt-0.5">{change.scg_description.slice(0, 100)}{change.scg_description.length > 100 ? '...' : ''}</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
